// components/SearchProfessionals.tsx
// VERSIÓN CON INDICADOR DE DISPONIBILIDAD POR SUSCRIPCIÓN

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import './AnimatedCovers.css';

interface Professional {
  id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  professional_type: 'salud' | 'belleza';
  business_name?: string;
  business_description?: string;
  picture?: string;
  rating?: number;
  next_availability?: string;
  services_count?: number;
  starting_price?: number;
  // ✅ NUEVOS CAMPOS PARA SUSCRIPCIÓN
  subscription_status?: string;
  trial_ends_at?: string;
  is_available_for_booking?: boolean;
}

interface SearchFilters {
  type: string;
  especialidad: string;
  search: string;
}

const SearchProfessionals: React.FC = () => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState<Professional[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [filters, setFilters] = useState<SearchFilters>({
    type: '',
    especialidad: '',
    search: ''
  });

  // Mostrar/ocultar filtros avanzados (engranaje)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasActiveAdvanced = !!filters.type || !!filters.especialidad;

  // Especialidades por tipo
  const especialidades = {
    salud: [
      'Medicina General', 'Cardiología', 'Dermatología', 'Neurología', 
      'Pediatría', 'Ginecología', 'Traumatología', 'Psicología', 'Nutrición',
      'Flebología'
    ],
    belleza: [
      'Peluquería', 'Estética Facial', 'Manicura', 'Masajes', 'Maquillaje',
      'Tratamientos Corporales', 'Depilación', 'Extensiones', 'Barbería'
    ]
  };

  // Cargar profesionales al montar el componente
  useEffect(() => {
    fetchProfessionals();
  }, []);

  // Filtrar profesionales cuando cambian los filtros
  useEffect(() => {
    filterProfessionals();
  }, [filters, professionals]);

  const fetchProfessionals = async () => {
    setIsLoading(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/professionals/search`);
      
      if (!response.ok) {
        throw new Error('Error al cargar profesionales');
      }

      const data = await response.json();
      setProfessionals(data.professionals || []);
    } catch (error) {
      console.error('Error fetching professionals:', error);
      setError('Error al cargar los profesionales. Intenta nuevamente.');
      setProfessionals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterProfessionals = () => {
    let filtered = [...professionals];

    // Filtrar por tipo
    if (filters.type) {
      filtered = filtered.filter(prof => prof.professional_type === filters.type);
    }

    // Filtrar por especialidad
    if (filters.especialidad) {
      filtered = filtered.filter(prof => 
        prof.especialidad.toLowerCase().includes(filters.especialidad.toLowerCase())
      );
    }

    // Filtrar por búsqueda de texto
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(prof => 
        prof.nombre.toLowerCase().includes(searchLower) ||
        prof.apellido.toLowerCase().includes(searchLower) ||
        prof.especialidad.toLowerCase().includes(searchLower) ||
        (prof.business_name && prof.business_name.toLowerCase().includes(searchLower))
      );
    }

    setFilteredProfessionals(filtered);
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Reset especialidad when type changes
      ...(key === 'type' && { especialidad: '' })
    }));
  };

  const clearFilters = () => {
    setFilters({ type: '', especialidad: '', search: '' });
  };

  const getAvailableEspecialidades = () => {
    if (!filters.type) return [];
    return especialidades[filters.type as keyof typeof especialidades] || [];
  };

  const formatProfessionalType = (type: string) => {
    return type === 'salud' ? 'Salud' : 'Belleza';
  };

  // Función para mostrar el conteo de servicios
  const displayServicesCount = (professional: Professional) => {
    if (!professional.services_count) {
      return null;
    }
    
    return (
      <div>
        {professional.services_count} servicio{professional.services_count !== 1 ? 's' : ''}
      </div>
    );
  };

  // Función para mostrar el rating
  const displayRating = (professional: Professional) => {
    if (!professional.rating) {
      return null;
    }
    
    return (
      <div className="flex items-center">
        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        {professional.rating}
      </div>
    );
  };

  // ✅ NUEVA FUNCIÓN: Renderizar badge de disponibilidad
  const renderAvailabilityBadge = (professional: Professional) => {
    // Si no está disponible para booking, mostrar badge
    if (professional.is_available_for_booking === false) {
      return (
        <div className="absolute top-4 left-4 z-10">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-600/90 text-white shadow-lg">
            No Disponible
          </span>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center pt-20">
            <LoadingSpinner size={48} label="Cargando profesionales..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header compacto */}
        <div className="flex items-end justify-between gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Lista de profesionales
          </h1>
          <span className="text-sm text-gray-500 flex-shrink-0 pb-1">
            {filteredProfessionals.length} {filteredProfessionals.length === 1 ? 'profesional' : 'profesionales'}
          </span>
        </div>

        {/* Barra de búsqueda compacta: lupa + engranaje */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            {/* Búsqueda por texto con lupa */}
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar por nombre o especialidad..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Engranaje: búsqueda avanzada */}
            <button
              onClick={() => setShowAdvanced(v => !v)}
              title="Búsqueda avanzada"
              aria-label="Búsqueda avanzada"
              className={`relative p-2.5 rounded-xl border shadow-sm transition-colors flex-shrink-0 ${
                showAdvanced || hasActiveAdvanced
                  ? 'bg-blue-50 border-blue-300 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {hasActiveAdvanced && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full ring-2 ring-white" />
              )}
            </button>
          </div>

          {/* Panel avanzado colapsable */}
          {showAdvanced && (
            <div className="mt-3 bg-white rounded-xl border border-gray-200 shadow-sm p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Todos los tipos</option>
                <option value="salud">Salud</option>
                <option value="belleza">Belleza</option>
              </select>

              <select
                value={filters.especialidad}
                onChange={(e) => handleFilterChange('especialidad', e.target.value)}
                disabled={!filters.type}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 bg-white"
              >
                <option value="">Todas las especialidades</option>
                {getAvailableEspecialidades().map(esp => (
                  <option key={esp} value={esp}>{esp}</option>
                ))}
              </select>

              <button
                onClick={clearFilters}
                className="w-full px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>

        {/* Resultados */}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchProfessionals}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Intentar nuevamente
            </button>
          </div>
        ) : (
          <>
            {/* Grid de profesionales */}
            {filteredProfessionals.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProfessionals.map(professional => (
                  <div 
                    key={professional.id} 
                    className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 ${
                      professional.is_available_for_booking === false ? 'opacity-75' : ''
                    }`}
                  >
                    {/* Imagen del profesional */}
                    <div className={`h-48 relative overflow-hidden ${
                      professional.picture
                        ? 'bg-gradient-to-br from-blue-500 to-green-500'
                        : `animated-cover animated-cover--${professional.professional_type === 'salud' ? 'salud' : 'belleza'} animated-cover--variant-${professional.id % 4}`
                    }`}>
                      {/* ✅ Badge de disponibilidad */}
                      {renderAvailabilityBadge(professional)}

                      {professional.picture ? (
                        <img
                          src={professional.picture}
                          alt={`${professional.nombre} ${professional.apellido}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center relative z-10">
                          {/* Decoraciones flotantes */}
                          <span className="animated-cover__bubble animated-cover__bubble--1" aria-hidden="true" />
                          <span className="animated-cover__bubble animated-cover__bubble--2" aria-hidden="true" />
                          <span className="animated-cover__bubble animated-cover__bubble--3" aria-hidden="true" />

                          <div className="w-20 h-20 bg-white/25 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl ring-2 ring-white/40 relative z-10">
                            <span className="text-3xl font-bold text-white drop-shadow-md">
                              {professional.nombre.charAt(0)}{professional.apellido.charAt(0)}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Badge de tipo */}
                      <div className="absolute top-4 right-4 z-20">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${
                          professional.professional_type === 'salud'
                            ? 'bg-blue-600/80'
                            : 'bg-pink-600/80'
                        }`}>
                          {formatProfessionalType(professional.professional_type)}
                        </span>
                      </div>
                    </div>

                    {/* Información del profesional */}
                    <div className="p-6">
                      <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {professional.business_name || `${professional.nombre} ${professional.apellido}`}
                        </h3>
                        <p className="text-blue-600 font-medium">
                          {professional.especialidad}
                        </p>
                        {professional.business_description && (
                          <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                            {professional.business_description}
                          </p>
                        )}
                      </div>

                      {/* ✅ Mensaje de no disponibilidad */}
                      {professional.is_available_for_booking === false && (
                        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-gray-700 text-sm font-medium">
                            ⚠️ Este profesional no está aceptando reservas en este momento
                          </p>
                        </div>
                      )}

                      {/* Stats */}
                      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                        {displayRating(professional)}
                        
                        {displayServicesCount(professional)}
                        
                        {professional.starting_price && (
                          <div className="font-medium text-green-600">
                            Desde ${professional.starting_price}
                          </div>
                        )}
                      </div>

                      {/* Próxima disponibilidad */}
                      {professional.next_availability && professional.is_available_for_booking !== false && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-green-800 text-sm font-medium">
                            Próxima disponibilidad: {professional.next_availability}
                          </p>
                        </div>
                      )}

                      {/* Botones de acción */}
                      <div className="flex gap-3">
                        <Link
                          to={`/p/${professional.id}`}
                          className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors text-center"
                        >
                          Ver Perfil
                        </Link>
                        
                        {/* ✅ Botón de agendar condicional */}
                        {professional.is_available_for_booking === false ? (
                          <button
                            disabled
                            className="flex-1 bg-gray-300 text-gray-500 py-2 px-4 rounded-lg font-medium cursor-not-allowed text-center"
                          >
                            No Disponible
                          </button>
                        ) : (
                          <Link
                            to={`/p/${professional.id}`}
                            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors text-center"
                          >
                            Agendar Cita
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No se encontraron profesionales
                </h3>
                <p className="text-gray-500 mb-4">
                  Intenta ajustar los filtros de búsqueda
                </p>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Mostrar todos los profesionales
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchProfessionals;